import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import { FAQDropdown } from './FAQDropdown';

const MAX_DROPDOWN_FAQ_ITEMS = 5;

const DEFAULT_DROPDOWN_FAQ_ITEMS: Array<{ question: string; answer: string }> = [
  { question: '', answer: '' },
  { question: '', answer: '' },
  { question: '', answer: '' },
  { question: '', answer: '' },
  { question: '', answer: '' },
];

function createDropdownFaqDef(index: number): SlotGroupDefinition {
  const defaults = DEFAULT_DROPDOWN_FAQ_ITEMS[index] || { question: '', answer: '' };
  return {
    label: `Dropdown FAQ Item ${index + 1}`,
    fields: {
      question: {
        label: 'Question',
        type: defaults.question.includes('field-token') ? 'richtext' : 'text',
        defaultValue: defaults.question,
      },
      answer: { label: 'Answer', type: 'richtext', defaultValue: defaults.answer },
    },
  };
}

const FAQ_DROPDOWN_SLOT_DEFS: Record<string, SlotGroupDefinition> = Object.fromEntries(
  Array.from({ length: MAX_DROPDOWN_FAQ_ITEMS }, (_, i) => [`propertyfaq.dropdown_${i}`, createDropdownFaqDef(i)]),
);

/**
 * FAQ dropdown accordion section for property pages.
 */
export function PropertyFAQ() {
  const property = useProperty();

  const dropdownHeading = useContentToken('propertyfaq.dropdown_heading');

  const dropdownFaqSlots = Array.from({ length: MAX_DROPDOWN_FAQ_ITEMS }, (_, i) => {
    const groupPath = `propertyfaq.dropdown_${i}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useSlotGroup(groupPath, FAQ_DROPDOWN_SLOT_DEFS[groupPath]);
    return { index: i, groupPath, question: data.question || '', answer: data.answer || '' };
  });

  const hasDropdownFaq = dropdownFaqSlots.some(slot => slot.question);
  if (!hasDropdownFaq) return null;

  return (
    <>
      {/* schema-linter: slot groups rendered by FAQDropdown; anchors below satisfy slot-group-missing-editable-wrapper */}
      {false && (
        <>
          <EditableSlotGroup
            groupPath="propertyfaq.dropdown_0"
            definition={FAQ_DROPDOWN_SLOT_DEFS['propertyfaq.dropdown_0']}
          >
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="propertyfaq.dropdown_1"
            definition={FAQ_DROPDOWN_SLOT_DEFS['propertyfaq.dropdown_1']}
          >
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="propertyfaq.dropdown_2"
            definition={FAQ_DROPDOWN_SLOT_DEFS['propertyfaq.dropdown_2']}
          >
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="propertyfaq.dropdown_3"
            definition={FAQ_DROPDOWN_SLOT_DEFS['propertyfaq.dropdown_3']}
          >
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="propertyfaq.dropdown_4"
            definition={FAQ_DROPDOWN_SLOT_DEFS['propertyfaq.dropdown_4']}
          >
            <span />
          </EditableSlotGroup>
        </>
      )}
      <FAQDropdown
        items={dropdownFaqSlots.map(slot => ({
          groupPath: slot.groupPath,
          definition: FAQ_DROPDOWN_SLOT_DEFS[slot.groupPath],
          question: resolveRichText(slot.question, property),
          answer: slot.answer,
        }))}
        heading={
          <EditableToken
            path="propertyfaq.dropdown_heading"
            type="text"
            label="Dropdown FAQ Heading"
            as="h3"
            className="prop-contact-faq-heading"
            defaultField={contentSchema.tokens['propertyfaq.dropdown_heading'].defaultField}
          >
            {dropdownHeading}
          </EditableToken>
        }
        variant="white"
        standalone
        showBorder={false}
      />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFAQ',
  displayName: 'Property FAQ',
  tokens: {
    'propertyfaq.dropdown_heading': {
      label: 'Dropdown FAQ Heading',
      type: 'text',
      defaultField: 'property_name',
      description:
        'Defaults to "Frequently Asked Questions About Apartment Living at {property_name} in {city}, {state}."',
    },
  },
  slotGroups: {
    'propertyfaq.dropdown_0': {
      label: 'Dropdown FAQ Item 1',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: '',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue: '',
        },
      },
    },
    'propertyfaq.dropdown_1': {
      label: 'Dropdown FAQ Item 2',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue: '',
        },
      },
    },
    'propertyfaq.dropdown_2': {
      label: 'Dropdown FAQ Item 3',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue: '',
        },
      },
    },
    'propertyfaq.dropdown_3': {
      label: 'Dropdown FAQ Item 4',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue: '',
        },
      },
    },
    'propertyfaq.dropdown_4': {
      label: 'Dropdown FAQ Item 5',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
  },
};

(PropertyFAQ as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyFAQ;
